import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Download, Upload, Lock, Unlock, Shield, Info, Eye, EyeOff, 
  FileText, FileJson, File, Key, Sparkles, Check, Copy
} from 'lucide-react';

// ========== ENCRYPTION ALGORITHMS (INLINE) ==========

// AES-256-like encryption using Web Crypto API
const encryptAES = async (text, password) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const passwordData = encoder.encode(password);
  
  // Generate key from password
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passwordData,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );
  
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const key = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt']
  );
  
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv },
    key,
    data
  );
  
  // Combine salt + iv + encrypted data
  const result = new Uint8Array(salt.length + iv.length + encrypted.byteLength);
  result.set(salt, 0);
  result.set(iv, salt.length);
  result.set(new Uint8Array(encrypted), salt.length + iv.length);
  
  return btoa(String.fromCharCode(...result));
};

const decryptAES = async (encryptedBase64, password) => {
  const encryptedData = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));
  
  const salt = encryptedData.slice(0, 16);
  const iv = encryptedData.slice(16, 28);
  const data = encryptedData.slice(28);
  
  const encoder = new TextEncoder();
  const passwordData = encoder.encode(password);
  
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passwordData,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );
  
  const key = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );
  
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv },
    key,
    data
  );
  
  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
};

// XOR Cipher (fast, less secure)
const encryptXOR = (text, password) => {
  const encoder = new TextEncoder();
  const textBytes = encoder.encode(text);
  const passBytes = encoder.encode(password);
  const encrypted = new Uint8Array(textBytes.length);
  
  for (let i = 0; i < textBytes.length; i++) {
    encrypted[i] = textBytes[i] ^ passBytes[i % passBytes.length];
  }
  
  return btoa(String.fromCharCode(...encrypted));
};

const decryptXOR = (encryptedBase64, password) => {
  const encryptedStr = atob(encryptedBase64);
  const encrypted = new Uint8Array(encryptedStr.split('').map(c => c.charCodeAt(0)));
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();
  const passBytes = encoder.encode(password);
  
  const decrypted = new Uint8Array(encrypted.length);
  for (let i = 0; i < encrypted.length; i++) {
    decrypted[i] = encrypted[i] ^ passBytes[i % passBytes.length];
  }
  
  return decoder.decode(decrypted);
};

// Caesar Cipher (simple substitution)
const encryptCaesar = (text, shift) => {
  return text.replace(/[a-zA-Z]/g, (char) => {
    const code = char.charCodeAt(0);
    const isUpperCase = code >= 65 && code <= 90;
    const base = isUpperCase ? 65 : 97;
    return String.fromCharCode(((code - base + shift) % 26) + base);
  });
};

const decryptCaesar = (text, shift) => {
  return encryptCaesar(text, 26 - shift);
};

// Base64 Encoding (not encryption, but useful)
const encodeBase64 = (text) => {
  return btoa(unescape(encodeURIComponent(text)));
};

const decodeBase64 = (text) => {
  return decodeURIComponent(escape(atob(text)));
};

// ========== MAIN COMPONENT ==========
const Encryption = () => {
  // Text Encryption State
  const [textMode, setTextMode] = useState('encrypt');
  const [textAlgorithm, setTextAlgorithm] = useState('aes');
  const [textInput, setTextInput] = useState('');
  const [textPassword, setTextPassword] = useState('');
  const [textOutput, setTextOutput] = useState('');
  const [showTextPassword, setShowTextPassword] = useState(false);
  const [caesarShift, setCaesarShift] = useState(3);
  const [processing, setProcessing] = useState(false);
  
  // File Encryption State
  const [fileMode, setFileMode] = useState('encrypt');
  const [fileAlgorithm, setFileAlgorithm] = useState('aes');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePassword, setFilePassword] = useState('');
  const [showFilePassword, setShowFilePassword] = useState(false);
  const [encryptedFileData, setEncryptedFileData] = useState(null);
  
  // JSON Encryption State
  const [jsonMode, setJsonMode] = useState('encrypt');
  const [jsonInput, setJsonInput] = useState('');
  const [jsonPassword, setJsonPassword] = useState('');
  const [jsonOutput, setJsonOutput] = useState('');
  const [showJsonPassword, setShowJsonPassword] = useState(false);
  
  const fileInputRef = useRef(null);

  // Process Text
  const processText = async () => {
    if (!textInput.trim()) {
      toast.error('Please enter some text');
      return;
    }
    
    if (textAlgorithm !== 'base64' && textAlgorithm !== 'caesar' && !textPassword) {
      toast.error('Please enter a password');
      return;
    }
    
    setProcessing(true);
    
    try {
      let result;
      
      if (textMode === 'encrypt') {
        switch (textAlgorithm) {
          case 'aes':
            result = await encryptAES(textInput, textPassword);
            break;
          case 'xor':
            result = encryptXOR(textInput, textPassword);
            break;
          case 'caesar':
            result = encryptCaesar(textInput, caesarShift);
            break;
          case 'base64':
            result = encodeBase64(textInput);
            break;
          default:
            throw new Error('Unknown algorithm');
        }
        toast.success('Text encrypted successfully!');
      } else {
        switch (textAlgorithm) {
          case 'aes':
            result = await decryptAES(textInput, textPassword);
            break;
          case 'xor':
            result = decryptXOR(textInput, textPassword);
            break;
          case 'caesar':
            result = decryptCaesar(textInput, caesarShift);
            break;
          case 'base64':
            result = decodeBase64(textInput);
            break;
          default:
            throw new Error('Unknown algorithm');
        }
        toast.success('Text decrypted successfully!');
      }
      
      setTextOutput(result);
    } catch (error) {
      console.error('Processing error:', error);
      toast.error(error.message || 'Failed to process text');
      setTextOutput('');
    } finally {
      setProcessing(false);
    }
  };

  // Handle File Upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setEncryptedFileData(null);
      toast.success('File loaded successfully');
    }
  };

  // Process File
  const processFile = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    
    if (!filePassword) {
      toast.error('Please enter a password');
      return;
    }
    
    setProcessing(true);
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const fileContent = e.target.result;
          let result;
          
          if (fileMode === 'encrypt') {
            const base64Content = btoa(fileContent);
            if (fileAlgorithm === 'aes') {
              result = await encryptAES(base64Content, filePassword);
            } else {
              result = encryptXOR(base64Content, filePassword);
            }
            
            const blob = new Blob([result], { type: 'text/plain' });
            setEncryptedFileData({ blob, filename: selectedFile.name + '.encrypted' });
            toast.success('File encrypted successfully!');
          } else {
            if (fileAlgorithm === 'aes') {
              result = await decryptAES(fileContent, filePassword);
            } else {
              result = decryptXOR(fileContent, filePassword);
            }
            
            const binaryContent = atob(result);
            const bytes = new Uint8Array(binaryContent.length);
            for (let i = 0; i < binaryContent.length; i++) {
              bytes[i] = binaryContent.charCodeAt(i);
            }
            
            const blob = new Blob([bytes]);
            const originalName = selectedFile.name.replace('.encrypted', '');
            setEncryptedFileData({ blob, filename: originalName });
            toast.success('File decrypted successfully!');
          }
        } catch (error) {
          console.error('File processing error:', error);
          toast.error('Failed to process file - wrong password or corrupted data');
        } finally {
          setProcessing(false);
        }
      };
      
      reader.readAsBinaryString(selectedFile);
    } catch (error) {
      console.error('File read error:', error);
      toast.error('Failed to read file');
      setProcessing(false);
    }
  };

  // Download processed file
  const downloadFile = () => {
    if (!encryptedFileData) return;
    
    const url = URL.createObjectURL(encryptedFileData.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = encryptedFileData.filename;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('File downloaded!');
  };

  // Process JSON
  const processJSON = async () => {
    if (!jsonInput.trim()) {
      toast.error('Please enter JSON data');
      return;
    }
    
    if (!jsonPassword) {
      toast.error('Please enter a password');
      return;
    }
    
    setProcessing(true);
    
    try {
      let result;
      
      if (jsonMode === 'encrypt') {
        // Validate JSON first
        JSON.parse(jsonInput);
        result = await encryptAES(jsonInput, jsonPassword);
        toast.success('JSON encrypted successfully!');
      } else {
        result = await decryptAES(jsonInput, jsonPassword);
        // Validate decrypted JSON
        JSON.parse(result);
        // Pretty print
        result = JSON.stringify(JSON.parse(result), null, 2);
        toast.success('JSON decrypted successfully!');
      }
      
      setJsonOutput(result);
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON format');
      } else {
        toast.error(error.message || 'Failed to process JSON');
      }
      setJsonOutput('');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="text-center space-y-4">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
          Encryption Toolkit
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Encrypt and decrypt text, files, and JSON with military-grade encryption algorithms. All processing happens locally.
        </p>
      </div>

      <Tabs defaultValue="text" className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-[800px] mx-auto mb-8">
          <TabsTrigger value="text" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Text
          </TabsTrigger>
          <TabsTrigger value="file" className="flex items-center gap-2">
            <File className="w-4 h-4" />
            Files
          </TabsTrigger>
          <TabsTrigger value="json" className="flex items-center gap-2">
            <FileJson className="w-4 h-4" />
            JSON
          </TabsTrigger>
          <TabsTrigger value="help" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Algorithms
          </TabsTrigger>
        </TabsList>

        {/* Text Tab */}
        <TabsContent value="text" className="space-y-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left: Input */}
            <Card className="border-border/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Key className="w-5 h-5 text-primary" />
                  Text {textMode === 'encrypt' ? 'Encryption' : 'Decryption'}
                </CardTitle>
                <CardDescription>
                  {textMode === 'encrypt' ? 'Secure your text with encryption' : 'Decrypt your encrypted text'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex gap-2">
                  <Button
                    onClick={() => setTextMode('encrypt')}
                    variant={textMode === 'encrypt' ? 'default' : 'outline'}
                    className="flex-1"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Encrypt
                  </Button>
                  <Button
                    onClick={() => setTextMode('decrypt')}
                    variant={textMode === 'decrypt' ? 'default' : 'outline'}
                    className="flex-1"
                  >
                    <Unlock className="w-4 h-4 mr-2" />
                    Decrypt
                  </Button>
                </div>

                <div className="space-y-3">
                  <Label className="text-base font-medium">Algorithm</Label>
                  <Select value={textAlgorithm} onValueChange={setTextAlgorithm}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select algorithm" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aes">AES-256-GCM (Military Grade)</SelectItem>
                      <SelectItem value="xor">XOR Cipher (Fast)</SelectItem>
                      <SelectItem value="caesar">Caesar Cipher (Classic)</SelectItem>
                      <SelectItem value="base64">Base64 (Encoding)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="text-input" className="text-base font-medium">
                    {textMode === 'encrypt' ? 'Plain Text' : 'Encrypted Text'}
                  </Label>
                  <Textarea
                    id="text-input"
                    placeholder={textMode === 'encrypt' ? 'Enter text to encrypt...' : 'Enter encrypted text to decrypt...'}
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    rows={6}
                    className="min-h-[150px] resize-none font-mono text-sm"
                  />
                </div>

                {textAlgorithm === 'caesar' ? (
                  <div className="space-y-3">
                    <Label className="text-base font-medium">Shift Amount</Label>
                    <Input
                      type="number"
                      min="1"
                      max="25"
                      value={caesarShift}
                      onChange={(e) => setCaesarShift(parseInt(e.target.value) || 3)}
                      className="h-11"
                    />
                  </div>
                ) : textAlgorithm !== 'base64' && (
                  <div className="space-y-3">
                    <Label htmlFor="text-password" className="text-base font-medium flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="text-password"
                        type={showTextPassword ? 'text' : 'password'}
                        placeholder="Enter encryption password"
                        value={textPassword}
                        onChange={(e) => setTextPassword(e.target.value)}
                        className="h-11 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowTextPassword(!showTextPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showTextPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}

                <Button 
                  onClick={processText}
                  disabled={processing || !textInput}
                  className="w-full h-12 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white font-bold"
                >
                  {processing ? (
                    'Processing...'
                  ) : (
                    <>
                      {textMode === 'encrypt' ? <Lock className="w-4 h-4 mr-2" /> : <Unlock className="w-4 h-4 mr-2" />}
                      {textMode === 'encrypt' ? 'Encrypt Text' : 'Decrypt Text'}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Right: Output */}
            <Card className="border-border/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Sparkles className="w-5 h-5 text-accent" />
                  Result
                </CardTitle>
                <CardDescription>
                  {textMode === 'encrypt' ? 'Encrypted output' : 'Decrypted output'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {textOutput ? (
                  <>
                    <div className="bg-gradient-to-b from-card to-card/50 rounded-xl p-4 border border-border min-h-[300px]">
                      <Textarea
                        value={textOutput}
                        readOnly
                        rows={12}
                        className="min-h-[280px] resize-none font-mono text-sm bg-transparent border-0 focus-visible:ring-0"
                      />
                    </div>

                    <div className="flex gap-3">
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(textOutput);
                          toast.success('Copied to clipboard!');
                        }}
                        variant="outline"
                        className="flex-1 h-11"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </Button>
                      
                      <Button
                        onClick={() => {
                          const blob = new Blob([textOutput], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `${textMode}-${Date.now()}.txt`;
                          link.click();
                          URL.revokeObjectURL(url);
                          toast.success('Saved as file!');
                        }}
                        className="flex-1 h-11 bg-primary"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Save
                      </Button>
                    </div>

                    <Alert className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
                      <Check className="w-5 h-5 text-primary" />
                      <AlertDescription>
                        <strong>Success!</strong> Your text has been {textMode === 'encrypt' ? 'encrypted' : 'decrypted'} 
                        using {textAlgorithm.toUpperCase()} algorithm.
                      </AlertDescription>
                    </Alert>
                  </>
                ) : (
                  <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center space-y-4 border-2 border-dashed border-border rounded-xl">
                    <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                      {textMode === 'encrypt' ? <Lock className="w-8 h-8 text-muted-foreground" /> : <Unlock className="w-8 h-8 text-muted-foreground" />}
                    </div>
                    <div>
                      <p className="text-muted-foreground font-medium">No output yet</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Enter your text and click the button to see results
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* File Tab */}
        <TabsContent value="file" className="space-y-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left: Upload & Config */}
            <Card className="border-border/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <File className="w-5 h-5 text-primary" />
                  File {fileMode === 'encrypt' ? 'Encryption' : 'Decryption'}
                </CardTitle>
                <CardDescription>
                  {fileMode === 'encrypt' ? 'Encrypt any file type' : 'Decrypt encrypted files'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex gap-2">
                  <Button
                    onClick={() => setFileMode('encrypt')}
                    variant={fileMode === 'encrypt' ? 'default' : 'outline'}
                    className="flex-1"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Encrypt
                  </Button>
                  <Button
                    onClick={() => setFileMode('decrypt')}
                    variant={fileMode === 'decrypt' ? 'default' : 'outline'}
                    className="flex-1"
                  >
                    <Unlock className="w-4 h-4 mr-2" />
                    Decrypt
                  </Button>
                </div>

                <div className="space-y-3">
                  <Label className="text-base font-medium">Algorithm</Label>
                  <Select value={fileAlgorithm} onValueChange={setFileAlgorithm}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select algorithm" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aes">AES-256-GCM (Recommended)</SelectItem>
                      <SelectItem value="xor">XOR Cipher (Faster)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-4">
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    className="w-full h-12"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {selectedFile ? 'Change File' : 'Select File'}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  
                  {selectedFile && (
                    <div className="p-4 bg-muted/30 rounded-lg border border-border">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <File className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{selectedFile.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(selectedFile.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <Label htmlFor="file-password" className="text-base font-medium flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="file-password"
                      type={showFilePassword ? 'text' : 'password'}
                      placeholder="Enter encryption password"
                      value={filePassword}
                      onChange={(e) => setFilePassword(e.target.value)}
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowFilePassword(!showFilePassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showFilePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  onClick={processFile}
                  disabled={processing || !selectedFile || !filePassword}
                  className="w-full h-12 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white font-bold"
                >
                  {processing ? (
                    'Processing...'
                  ) : (
                    <>
                      {fileMode === 'encrypt' ? <Lock className="w-4 h-4 mr-2" /> : <Unlock className="w-4 h-4 mr-2" />}
                      {fileMode === 'encrypt' ? 'Encrypt File' : 'Decrypt File'}
                    </>
                  )}
                </Button>

                <Alert>
                  <Info className="w-4 h-4" />
                  <AlertDescription className="text-sm">
                    Files are processed entirely in your browser. No data is uploaded to any server.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            {/* Right: Result */}
            <Card className="border-border/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Sparkles className="w-5 h-5 text-accent" />
                  Processed File
                </CardTitle>
                <CardDescription>Download your {fileMode === 'encrypt' ? 'encrypted' : 'decrypted'} file</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {encryptedFileData ? (
                  <>
                    <div className="p-8 bg-gradient-to-b from-card to-card/50 rounded-xl border border-border flex flex-col items-center justify-center space-y-4">
                      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                        <File className="w-10 h-10 text-primary" />
                      </div>
                      <div className="text-center">
                        <p className="font-medium">{encryptedFileData.filename}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Ready to download
                        </p>
                      </div>
                    </div>

                    <Button 
                      onClick={downloadFile}
                      className="w-full h-12 bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white font-bold"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download File
                    </Button>

                    <Alert className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
                      <Check className="w-5 h-5 text-primary" />
                      <AlertDescription>
                        <strong>Success!</strong> Your file has been {fileMode === 'encrypt' ? 'encrypted' : 'decrypted'} 
                        using {fileAlgorithm.toUpperCase()}-256 algorithm.
                      </AlertDescription>
                    </Alert>

                    <div className="bg-muted/30 rounded-lg p-4 border border-border">
                      <h4 className="font-semibold mb-2 text-sm">Important</h4>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li>• Keep your password secure - it cannot be recovered</li>
                        <li>• Use the same algorithm for decryption</li>
                        <li>• Store encrypted files safely</li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center space-y-4 border-2 border-dashed border-border rounded-xl">
                    <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                      <File className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-muted-foreground font-medium">No processed file yet</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Upload a file and process it to see results
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* JSON Tab */}
        <TabsContent value="json" className="space-y-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left: Input */}
            <Card className="border-border/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <FileJson className="w-5 h-5 text-primary" />
                  JSON {jsonMode === 'encrypt' ? 'Encryption' : 'Decryption'}
                </CardTitle>
                <CardDescription>
                  {jsonMode === 'encrypt' ? 'Encrypt JSON data securely' : 'Decrypt encrypted JSON'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex gap-2">
                  <Button
                    onClick={() => setJsonMode('encrypt')}
                    variant={jsonMode === 'encrypt' ? 'default' : 'outline'}
                    className="flex-1"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Encrypt
                  </Button>
                  <Button
                    onClick={() => setJsonMode('decrypt')}
                    variant={jsonMode === 'decrypt' ? 'default' : 'outline'}
                    className="flex-1"
                  >
                    <Unlock className="w-4 h-4 mr-2" />
                    Decrypt
                  </Button>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="json-input" className="text-base font-medium">
                    {jsonMode === 'encrypt' ? 'JSON Data' : 'Encrypted JSON'}
                  </Label>
                  <Textarea
                    id="json-input"
                    placeholder={jsonMode === 'encrypt' ? '{"key": "value", "data": [1, 2, 3]}' : 'Enter encrypted JSON...'}
                    value={jsonInput}
                    onChange={(e) => setJsonInput(e.target.value)}
                    rows={10}
                    className="min-h-[250px] resize-none font-mono text-sm"
                  />
                </div>

                <div className="space-y-3">
                  <Label htmlFor="json-password" className="text-base font-medium flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="json-password"
                      type={showJsonPassword ? 'text' : 'password'}
                      placeholder="Enter encryption password"
                      value={jsonPassword}
                      onChange={(e) => setJsonPassword(e.target.value)}
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowJsonPassword(!showJsonPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showJsonPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  onClick={processJSON}
                  disabled={processing || !jsonInput || !jsonPassword}
                  className="w-full h-12 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white font-bold"
                >
                  {processing ? (
                    'Processing...'
                  ) : (
                    <>
                      {jsonMode === 'encrypt' ? <Lock className="w-4 h-4 mr-2" /> : <Unlock className="w-4 h-4 mr-2" />}
                      {jsonMode === 'encrypt' ? 'Encrypt JSON' : 'Decrypt JSON'}
                    </>
                  )}
                </Button>

                <Alert>
                  <Info className="w-4 h-4" />
                  <AlertDescription className="text-sm">
                    JSON is validated before encryption and after decryption to ensure data integrity.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            {/* Right: Output */}
            <Card className="border-border/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Sparkles className="w-5 h-5 text-accent" />
                  Result
                </CardTitle>
                <CardDescription>
                  {jsonMode === 'encrypt' ? 'Encrypted JSON' : 'Decrypted JSON'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {jsonOutput ? (
                  <>
                    <div className="bg-gradient-to-b from-card to-card/50 rounded-xl p-4 border border-border min-h-[300px]">
                      <Textarea
                        value={jsonOutput}
                        readOnly
                        rows={14}
                        className="min-h-[350px] resize-none font-mono text-sm bg-transparent border-0 focus-visible:ring-0"
                      />
                    </div>

                    <div className="flex gap-3">
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(jsonOutput);
                          toast.success('Copied to clipboard!');
                        }}
                        variant="outline"
                        className="flex-1 h-11"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </Button>
                      
                      <Button
                        onClick={() => {
                          const blob = new Blob([jsonOutput], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `${jsonMode}-${Date.now()}.json`;
                          link.click();
                          URL.revokeObjectURL(url);
                          toast.success('Saved as file!');
                        }}
                        className="flex-1 h-11 bg-primary"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Save JSON
                      </Button>
                    </div>

                    <Alert className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
                      <Check className="w-5 h-5 text-primary" />
                      <AlertDescription>
                        <strong>Success!</strong> Your JSON has been {jsonMode === 'encrypt' ? 'encrypted' : 'decrypted'} 
                        using AES-256-GCM algorithm.
                      </AlertDescription>
                    </Alert>
                  </>
                ) : (
                  <div className="h-full min-h-[450px] flex flex-col items-center justify-center text-center space-y-4 border-2 border-dashed border-border rounded-xl">
                    <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                      <FileJson className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-muted-foreground font-medium">No output yet</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Enter valid JSON and click the button to see results
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Help Tab */}
        <TabsContent value="help">
          <Card className="border-border/50 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Shield className="w-6 h-6 text-primary" />
                Encryption Algorithms Explained
              </CardTitle>
              <CardDescription className="text-base">
                Understanding different encryption methods and their use cases
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                    <h3 className="text-lg font-semibold mb-2 text-primary flex items-center gap-2">
                      🛡️ AES-256-GCM
                    </h3>
                    <p className="text-muted-foreground text-sm mb-2">
                      <strong>Advanced Encryption Standard</strong> - Military-grade encryption used by governments worldwide.
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• 256-bit key length</li>
                      <li>• PBKDF2 key derivation (100,000 iterations)</li>
                      <li>• Authenticated encryption (GCM mode)</li>
                      <li>• Best for: Sensitive data, files, production use</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-secondary/5 border border-secondary/20">
                    <h3 className="text-lg font-semibold mb-2 text-secondary">⚡ XOR Cipher</h3>
                    <p className="text-muted-foreground text-sm mb-2">
                      Fast symmetric encryption using bitwise XOR operation.
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• Very fast processing</li>
                      <li>• Low computational cost</li>
                      <li>• Simple implementation</li>
                      <li>• Best for: Large files, quick encryption, testing</li>
                    </ul>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-accent/5 border border-accent/20">
                    <h3 className="text-lg font-semibold mb-2 text-accent">📜 Caesar Cipher</h3>
                    <p className="text-muted-foreground text-sm mb-2">
                      Classic substitution cipher shifting letters by a fixed amount.
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• Historical encryption method</li>
                      <li>• Easy to understand</li>
                      <li>• Works only with letters</li>
                      <li>• Best for: Learning, fun puzzles, NOT security</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-background border border-border">
                    <h3 className="text-lg font-semibold mb-2">🔤 Base64</h3>
                    <p className="text-muted-foreground text-sm mb-2">
                      Encoding method (not encryption) to represent binary data as text.
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• NOT encryption (easily reversible)</li>
                      <li>• Used for data transmission</li>
                      <li>• Makes data URL-safe</li>
                      <li>• Best for: Encoding, not security</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Alert className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
                <Info className="w-5 h-5 text-primary" />
                <AlertDescription>
                  <strong>Security Recommendation:</strong> For sensitive data, always use AES-256-GCM with a strong password 
                  (12+ characters, mixed case, numbers, symbols). Never share passwords through unsecure channels.
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="p-4 rounded-lg bg-muted/30">
                  <div className="text-2xl font-bold text-primary">AES-256</div>
                  <div className="text-sm text-muted-foreground">Military Grade</div>
                </div>
                <div className="p-4 rounded-lg bg-muted/30">
                  <div className="text-2xl font-bold text-secondary">100k</div>
                  <div className="text-sm text-muted-foreground">PBKDF2 Rounds</div>
                </div>
                <div className="p-4 rounded-lg bg-muted/30">
                  <div className="text-2xl font-bold text-accent">100%</div>
                  <div className="text-sm text-muted-foreground">Local Process</div>
                </div>
                <div className="p-4 rounded-lg bg-muted/30">
                  <div className="text-2xl font-bold text-purple-500">Zero</div>
                  <div className="text-sm text-muted-foreground">Server Upload</div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-card to-card/50 p-6 rounded-xl border border-border">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Key className="w-5 h-5 text-primary" />
                  Password Best Practices
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-sm mb-2 text-secondary">✅ Do</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Use 12+ characters</li>
                      <li>• Mix letters, numbers, symbols</li>
                      <li>• Use unique passwords</li>
                      <li>• Store in password manager</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm mb-2 text-destructive">❌ Don't</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Use common words or patterns</li>
                      <li>• Reuse passwords</li>
                      <li>• Share via email or chat</li>
                      <li>• Write down in plain text</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Encryption;
